import { Modal } from 'antd';
import React, { FC } from 'react';
import styled from 'styled-components';
import { LoadingIndicator } from '../LoadingIndicator';
import { ScrollCss } from '../ScrollContainer';

const StyledContent = styled.div<{
  scrollable?: boolean;
  scrollableHeight?: number;
}>`
  ${({ scrollable }) => (scrollable ? ScrollCss : null)}
  height: ${({ scrollable, scrollableHeight }) =>
    scrollable ? `${scrollableHeight ?? 500}px` : 'auto'};
`;

const StyledModal = styled(Modal)<{
  width?: number | string;
}>`
  width: ${({ width }) => width || 'auto'};
  border-radius: 8px !important;
  padding-bottom: 0px !important;
  padding: 16px 24px 24px 24px;
  .ant-modal-content {
    background-color: ${props => props.theme.color.pearl40};
    border-radius: 8px !important;

    .ant-modal-close-x {
      font-size: 24px;
      color: ${({ theme }) => theme.color.black};
      height: 56px;
      width: 56px;
      line-height: 56px;
    }

    .ant-modal-body {
      .ant-form {
        .ant-row {
          &.ant-form-item {
            border-bottom: 1px solid ${({ theme }) => theme.color.pearl};

            margin-bottom: 12px;
            padding-bottom: 12px;

            .ant-btn-link > .anticon {
              color: ${props => props.theme.vars.primaryColor};
            }

            .ant-col {
              .ant-form-item-control {
                .ant-form-item-children {
                  display: flex;
                  align-items: center;
                }
              }
            }

            &:nth-last-child(2),
            &:last-child {
              margin-bottom: 0;
              padding-bottom: 0;
              border-bottom: none;
            }

            &.header {
              .ant-typography {
                margin-bottom: 0;
                padding-top: 20px;
                display: flex;
                align-items: center;

                .anticon {
                  font-size: 35px;
                  margin-right: 10px;
                }
              }

              .ant-col {
                .ant-form-item-control {
                  .ant-form-item-children {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                  }
                }
              }

              .close {
                font-size: 20px;
                padding: 0;
                margin: 0;
                height: auto;
                line-height: 1;
              }
            }

            &.footer {
              display: flex;
              justify-content: center;
              align-items: center;
              padding-top: 10px;
            }
          }
        }
      }
    }
  }

  .ant-modal-header {
    padding: 32px 0 16px 0;
    background: transparent;
    text-align: center;
    border-bottom: none;

    .ant-modal-title {
      font-size: 20px;
      color: ${({ theme }) => theme.color.dark};
      color: ${({ theme }) => theme.color.dark};
      font-size: 24px !important;
      font-weight: normal;
      line-height: 1.2;

      .modal-title-icon {
        font-size: 50px;
        color: ${({ theme }) => theme.color.purple};
      }
    }
  }
  .ant-btn {
    min-width: 200px;
    padding: 0px 16px;
    margin: 0px 8px !important;
    height: 40px;
  }

  .ant-modal-footer {
    padding: 0px 32px 32px 32px;
    text-align: center;
    border-top: none;
  }
`;

export interface CustomModalProps {
  className?: string;
  centered?: boolean;
  closable?: boolean;
  footer?: JSX.Element;
  content: JSX.Element | string | React.ReactNode;
  destroyOnClose?: boolean;
  getContainer?: HTMLElement;
  wrapClassName?: string;
  title?: React.ReactNode;
  key?: string;
  scrollable?: boolean;
  scrollableHeight?: number;
  visible: boolean;
  isContentLoading?: boolean;
  width?: string | number;
  zIndex?: number;
  loading?: boolean;
  showLoading?: boolean;
  size?: 'small' | 'default' | 'large' | 'xlarge';
  onCancel?: () => void;
  onOk?: () => void;
  afterClose?: () => void;
}

const CustomModal: FC<CustomModalProps> = ({
  className,
  title,
  visible,
  footer = false,
  isContentLoading,
  content,
  onCancel,
  destroyOnClose,
  width,
  centered,
  closable,
  getContainer,
  wrapClassName,
  zIndex,
  key,
  scrollable,
  scrollableHeight,
  showLoading,
  onOk,
  afterClose,
  size,
}) => {
  const getContent = () => {
    return (
      <StyledContent
        scrollable={scrollable}
        scrollableHeight={scrollableHeight}
        className="content-container"
      >
        {content}
      </StyledContent>
    );
  };

  const getWidth = () => {
    if (width) {
      return width;
    }
    switch (size) {
      case 'small':
        return '40%';
      case 'large':
        return '70%';
      case 'xlarge':
        return '90%';
      case 'default':
      default:
        return '50%';
    }
  };

  return (
    <StyledModal
      className={className}
      centered={centered}
      closable={closable}
      footer={footer}
      destroyOnClose={destroyOnClose}
      getContainer={getContainer}
      wrapClassName={wrapClassName}
      title={title}
      key={key}
      visible={visible}
      width={getWidth()}
      zIndex={zIndex}
      onCancel={onCancel}
      onOk={onOk}
      afterClose={afterClose}
    >
      {showLoading ? (
        <LoadingIndicator spinning={isContentLoading}>
          {getContent()}
        </LoadingIndicator>
      ) : (
        getContent()
      )}
    </StyledModal>
  );
};

export { CustomModal };
